import { useCallback, useEffect, useMemo, useState } from 'react';
import { useEffectOnce } from 'react-use';
import percent from '~/assets/icons/percent.svg';
import { CurrencyIcon } from '~/components/collections';
import { Button, ButtonColors, ButtonHeight } from '~/components/ui/Button';
import { Text } from '~/components/ui/Text';
import { useWalletIcon } from '~/hooks';
import { rage, useObservable } from '~/rage';
import { BankOperationType, Currency } from '~/shared';
import { BankStages } from '../../models/Bank';
import {
	OverlayType,
	setActiveBankState,
	setOverlayTaxesData,
	setOverlayType,
	setOverlayVisible,
	useLayoutDispatch,
} from '../../redux';
import { AppBackButton } from '../AppBackButton';
import { AppBankCards } from '../AppBankCards';
import { DaysInput } from '../DaysInput';
import { HouseButton } from '../HouseButton';
import {
	StyledButtonContainer,
	StyledCardsContainer,
	StyledDaysInputContainer,
	StyledHead,
	StyledHousesContainer,
	StyledInfoContainer,
	StyledInfoRow,
} from './AppBankTaxes.styled';

const AppBankTaxes = () => {
	const dispatch = useLayoutDispatch();
	const [ownedHouses, setOwnedHouses] = useState<string[]>([]);
	const [activeHouse, setActiveHouse] = useState('');
	const [days, setDays] = useState<number>(0);
	const [loading, setLoading] = useState<boolean>(false);
	const [paidDays, setPaidDays] = useState<number>(0);
	const rubleIcon = useWalletIcon(Currency.Ruble, true);
	const fee = useObservable(rage.bank.getTransferFee(BankOperationType.TAX));
	const maxPayDays = rage.houses.getMaxPaidDays();
	const dayPrice = useMemo(
		() => (activeHouse == '' ? 0 : rage.houses.getPriceForDay(activeHouse)),
		[activeHouse],
	);

	const buttonText = useMemo(() => {
		if (days === 0) {
			return 'Выберите кол-во дней';
		}

		return 'Оплатить налоги';
	}, [days]);

	const handlePay = useCallback(() => {
		if (days === 0) {
			return;
		}

		setLoading(true);
		dispatch(setOverlayType(OverlayType.Taxes));
		dispatch(
			setOverlayTaxesData({
				amount: Math.floor(days * dayPrice),
				apartment: activeHouse,
				date: Date.now(),
			}),
		);

		rage.houses
			.pay(activeHouse, days)
			.then(() => {
				setLoading(false);
				setPaidDays(Math.min(paidDays + days, maxPayDays));
				dispatch(setOverlayVisible(true));
			})
			.catch((err) => rage.notifications.showException(err));
	}, [activeHouse, days]);

	useEffectOnce(() => {
		rage.houses
			.getOwnedHousesIds()
			.then((value) => {
				setOwnedHouses(value);
				if (value.length > 0) {
					setActiveHouse(value[0]);
				}
			})
			.catch((err) => rage.notifications.showException(err));
	});

	useEffect(() => {
		if (activeHouse) {
			rage.houses
				.getById(activeHouse)
				.then((house) => {
					setPaidDays(house?.daysPaid ?? 0);
				})
				.catch((err) => rage.notifications.showException(err));
		}
	}, [activeHouse]);

	return (
		<div>
			<StyledHead>
				<AppBackButton
					onClick={() => dispatch(setActiveBankState(BankStages.MAIN))}
				/>
				<Text>Налоги</Text>
			</StyledHead>
			<StyledCardsContainer>
				<AppBankCards compact />
			</StyledCardsContainer>
			<StyledHousesContainer>
				{ownedHouses.map((house) => (
					<HouseButton
						key={house}
						text={`Дом #${house}`}
						active={activeHouse === house}
						onClick={() => setActiveHouse(house)}
					/>
				))}
			</StyledHousesContainer>
			<StyledDaysInputContainer>
				<DaysInput
					value={days}
					changeHandler={setDays}
					payDays={paidDays}
					maxDays={maxPayDays}
				/>
			</StyledDaysInputContainer>
			<StyledInfoContainer>
				<StyledInfoRow>
					<Text>Вы заплатите</Text>
					<Text>
						{Math.floor(days * dayPrice + days * dayPrice * fee).toLocaleString(
							'ru-RU',
						)}
					</Text>
					<CurrencyIcon icon={rubleIcon} size="sm" />
				</StyledInfoRow>
				{fee ? (
					<StyledInfoRow>
						<Text>Комиссия банка</Text>
						<Text>{(fee * 100).toLocaleString('ru-RU')}</Text>
						<img src={percent} />
					</StyledInfoRow>
				) : null}
			</StyledInfoContainer>
			<StyledButtonContainer>
				<Button
					color={ButtonColors.Select}
					height={ButtonHeight.Height60}
					style={{ fontSize: 18 }}
					disabled={loading || days === 0}
					onClick={handlePay}
				>
					<Text>{buttonText}</Text>
				</Button>
			</StyledButtonContainer>
		</div>
	);
};

export { AppBankTaxes };
